import { Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Component, EventEmitter, Output } from '@angular/core';
import { Table } from 'primeng/table';
import { ListBase } from 'src/app/components/core/_infrastructure/classes/listBase';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { FilterSearchingBaseViewModel } from 'src/app/components/core/_infrastructure/types/common';
import { proxiedPropertiesOf } from 'src/app/components/core/_infrastructure/types/utils';
import { RecintoItemResultsViewModel, RecintoItemViewModel } from '../recinto.types';
import { DialogMode } from '../../types/enum';
import { CultivoViewModel } from '../../cultivos/cultivo.types';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-recinto-list',
    templateUrl: './recinto-list.component.html',
    styleUrls: ['./recinto-list.component.scss'],
})
export class RecintoListComponent extends ListBase implements OnInit, OnChanges {
    @Input() result: RecintoItemResultsViewModel;
    @Input() lastFilter: FilterSearchingBaseViewModel;
    @Input() mode: DialogMode;
    @Input() readonly: boolean;
    @Output() search = new EventEmitter<FilterSearchingBaseViewModel>();
    @Output() edit = new EventEmitter<number | null>();
    @Output() view = new EventEmitter<number | null>();
    @Output() delete = new EventEmitter<CultivoViewModel>();
    @Output() undoDelete = new EventEmitter<CultivoViewModel>();
    @Output() selectedCultivos = new EventEmitter<any>();

    @ViewChild('tableSearch') turboTable: Table;

    cols: any[] = [];
    title = 'Listado de Recintos y Cultivos';
    dialogMode = DialogMode;

    constructor(
        protected notificationService: NotificationService,
        private datepipe: DatePipe,
    ) {
        super(notificationService);
    }

    ngOnInit(): void {
        this.initCols();
        this.loading = false;
    }

    private initCols(): void {
        const recintoVM = proxiedPropertiesOf<RecintoItemViewModel>();
        this.cols = [];
        if (this.mode !== DialogMode.Basic) {
            this.cols = [{ field: 'ExpandLink', header: '', reduce: true }];
        }
        this.cols = [
            ...this.cols,
            ...[
                { field: 'Link', header: '', reduce: true },
                { field: recintoVM.Id?.toString(), header: 'Id', display: 'none' },
                { field: recintoVM.Titular?.toString(), header: 'Titular del recinto' },
                { field: recintoVM.Nif?.toString(), header: 'NIF' },
                { field: recintoVM.TipoRecinto?.toString(), header: 'Tipo Recinto' },
                { field: recintoVM.Provincia?.toString(), header: 'Provincia' },
                { field: recintoVM.Municipio?.toString(), header: 'Municipio' },
                { field: recintoVM.Agregado, header: 'Agregado' },
                { field: recintoVM.Zona, header: 'Zona' },
                { field: recintoVM.Poligono, header: 'Polígono' },
                { field: recintoVM.Parcela, header: 'Parcela' },
                { field: recintoVM.Recinto, header: 'Recinto' },
                { field: recintoVM.SuperficieTotal, header: 'Superficie Total (ha)' },
            ],
        ];
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.result?.currentValue) {
            this.result = { ...changes.result.currentValue };
            this.loading = false;
            setTimeout(() => {
                if (this.turboTable) {
                    this.turboTable.sortOrder = this.lastFilter?.Asc;
                    this.turboTable.sortField = this.lastFilter?.OrderBy;
                    this.turboTable.first = this.lastFilter?.PageNumber * this.result.PageSize;
                }
            }, 100);
        }
        if (changes?.mode?.currentValue) {
            this.initCols();
        }
    }

    onSearchLazy(event: any): void {
        if (!event.rows || event.sortField === 'Link' || event.sortField === 'DadoDeBaja') {
            return;
        }

        let pageNumber = event.first / event.rows;
        this.loading = true;

        const f: FilterSearchingBaseViewModel = {
            PageNumber: pageNumber,
            Asc: event.sortOrder,
            OrderBy: event.sortField || '',
        };

        this.search.emit(f);
    }

    onEdit(item: RecintoItemViewModel): void {
        this.edit.emit(item?.Id);
    }

    onView(item: RecintoItemViewModel): void {
        this.view.emit(item?.Id);
    }

    onDelete(item: CultivoViewModel): void {
        this.delete.emit(item);
    }

    onUndoDelete(item: CultivoViewModel): void {
        this.undoDelete.emit(item);
    }

    /**
     * This event is used only when in dialogMode.
     * It will have to tell which kind of selection the user has made.
     * The data selected will be raised alongside the type of selection.
     *
     * A partir de los cultivos seleccionados en la lista de recintos-cultivos,
     * se crea una lista nueva que contiene el recinto y dentro de su propiedad TwCultivoCollection, solo sus cultivos seleccionados.
     * Esto se hace así, porque se ha asociado el cultivo al checkbox.
     * Si se asociara el recinto entero al checkbox, la selección contendría el recinto y todos sus cultivos,
     * independientemente de que solo se seleccionara uno, de ahí que no valga la solución sencilla que se ha implementado en otros componentes.
     *
     * La lista de recintos-cultivos así compuesta se emite por evento para su procesamiento en el componente subscriptor.
     * @param $event Event
     * @param rowData RecintoItemViewModel
     * @param cultivo CultivoViewModel
     */
    onSelect($event: any, rowData: RecintoItemViewModel, cultivo: CultivoViewModel): void {
        // cultivo is ignored, because the proper values are at _selection
        if (this.mode === DialogMode.Dialog) {
            const cultivosSeleccionados: CultivoViewModel[] = this.turboTable?._selection;
            const IdsCultivosSeleccionados = cultivosSeleccionados?.map(cultivo => cultivo.Id); // obtenemos los Ids de los cultivos seleccionados.
            let recintosCultivos: RecintoItemViewModel[] = [];
            let contadorCultivosProcesados = 0;

            // Recorremos la lista original de recintos-cultivos.
            this.result.ResultList.forEach(recinto => {
                if (!recinto) return;
                if (contadorCultivosProcesados === IdsCultivosSeleccionados.length) {
                    // Optimización para evitarnos continuar si ya se han añadido todos los cultivos seleccionados.
                    return;
                }

                // @Revisión 2025/02/25: Solo debería haber un cultivo asociado al recinto.
                // Para cada recinto original, comparamos sus cultivos con el seleccionado.
                recinto.TwCultivoCollection?.forEach(cultivo => {
                    // Vamos creando una lista con los recintos-cultivos seleccionados.
                    // Si dado un cultivo, su recinto ya hubiera sido añadido a la lista, entonces añadimos el cultivo a su lista de cultivos.
                    const cultivoAIncluir = IdsCultivosSeleccionados.find(idCultivoSeleccionado => idCultivoSeleccionado === cultivo.Id);
                    if (cultivoAIncluir === undefined || cultivoAIncluir === null) {
                        return;
                    }
                    const recintoCultivoFound = recintosCultivos?.find(recinto => recinto.Id === cultivo.IdRecinto);
                    if (recintosCultivos?.length > 0 && recintoCultivoFound) {
                        // Añadimos el cultivo a los que ya hubiéramos añadido del actual recinto.
                        recintoCultivoFound.TwCultivoCollection?.push({ ...cultivo });
                    } else {
                        const nuevoRecinto = { ...recinto };
                        nuevoRecinto.TwCultivoCollection = [{ ...cultivo }];
                        recintosCultivos.push(nuevoRecinto!);
                    }
                    contadorCultivosProcesados++;
                });
            });
            this.selectedCultivos.emit({ selection: recintosCultivos, dblclick: false });
        }
    }

    /**
     * Un cultivo estará en lista negra si está desautorizado en alguna campaña..
     * Se controlará en el core, que el cultivo/recinto no esté vinculado a un periodo, con plazo activo.
     * @param item CultivoViewModel
     * @returns boolean
     */
    cultivoEsDesautorizado(item: CultivoViewModel): boolean {
        return item.Desautorizado;
    }

    showDesautorizadasMessage(item: CultivoViewModel): string {
        let desautorizadas = '';

        item.CampaniasDesautorizado.forEach((c, index) => {
            desautorizadas += `${c.Descripcion} el ${this.datepipe.transform(c.Fecha, 'dd/MM/yyyy HH:mm')}`;
            if (index < item.CampaniasDesautorizado.length - 1) {
                desautorizadas += ' | ';
            }
        });

        if (item.CampaniasDesautorizado?.length === 1) {
            desautorizadas = `Desautorizado en la campaña: ${desautorizadas}`;
        } else {
            desautorizadas = `Desautorizado en las campañas: ${desautorizadas}`;
        }

        return desautorizadas;
    }
}
